import { useState, useEffect, useRef, useMemo } from 'react';
import { pacientesStore, eventosCollection } from '../utils/firebase';
import { normISO } from '../utils/surgeryHelpers';

/*
  Estructura del documento en colección "eventos":
  {
    id: doc.id,
    event: {
      summary     : "Pending" | "Confirmada" | "Cancelada" | "Realizada"
      description : "AppointmentType=Sala X|Medico=...|Unidad Hospitalaroa=Hospital-HMAS-Los Cabos|..."
      start       : { dateTime: "2026-06-17T8:30",  timeZone: "UTC" }
      end         : { dateTime: "2026-06-17T10:30", timeZone: "UTC" }
      imagenologia      : { arcoC: bool, rayoxX: bool }
      materialEspecial  : []
      rentaEquipos      : []
      muestrapatologica : { postOperatorios: bool, trasOperatorios: bool }
    }
  }

  REGLAS:
  PENDIENTES  → event.summary === "Pending"    && event.start >= rangoDesde (sin límite superior)
  CONFIRMADAS → event.summary === "Confirmada" && event.start dentro del rango
  HISTÓRICO   → event.summary === "Realizada"  && event.start dentro del rango
  CANCELADAS  → event.summary === "Cancelada"  && event.start dentro del rango

  Nota: Pendientes/Confirmadas ya NO se mueven automáticamente a Histórico
  cuando pasa su hora programada — solo un cambio explícito de estado
  (Confirmar / Cancelar / Realizada) las mueve de pestaña.
*/

/* Convierte "A=1|B=2" en { A: "1", B: "2" } */
export const parseDesc = (str) => {
  if (!str) return {};
  return str.split('|').reduce((acc, par) => {
    const i = par.indexOf('=');
    if (i === -1) return acc;
    acc[par.slice(0, i).trim()] = par.slice(i + 1).trim();
    return acc;
  }, {});
};

/* normISO (utils/surgeryHelpers) normaliza "2026-06-17T8:30", "2026-10-3T01:30", etc. */

/* Valor especial para el selector de unidad: solo disponible para el
   perfil admin/soporte asignado a "Corporativo" (ver AgendaPage.js). Al
   usarse, se omite el filtro por unidad hospitalaria en las pestañas de
   lista e Indicadores (la Agenda del día siempre exige una unidad
   específica, nunca "todas"). */
export const TODAS_LAS_UNIDADES = '__TODAS_LAS_UNIDADES__';

/* Acceso a campos dentro de doc.event */
const getSummary = (doc) => (doc?.event?.summary || '').toLowerCase();
const getInicio  = (doc) => {
  const d = new Date(normISO(doc?.event?.start?.dateTime) || 0);
  return isNaN(d) ? null : d;
};
const getFin = (doc) => {
  const d = new Date(normISO(doc?.event?.end?.dateTime) || 0);
  return isNaN(d) ? null : d;
};
const getUH = (doc) => {
  const f = parseDesc(doc?.event?.description);
  return f['Unidad Hospitalaroa'] || f['Unidad Hospitalaria'] || '';
};

/* Requerimientos especiales — pueden ser objeto, array o bool */
const checkVal = (v) => {
  if (!v) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.values(v).some(Boolean);
  return Boolean(v);
};

export const tieneRequerimientos = (doc) => {
  const ev = doc?.event || {};
  const fields = parseDesc(ev.description);
  return checkVal(ev.imagenologia) || checkVal(ev.materialEspecial) ||
         checkVal(ev.rentaEquipos) || checkVal(ev.muestrapatologica) ||
         checkVal(ev.requerimientoEspecial) || checkVal(fields['RequerimientoEspecial']);
};

export const listaRequerimientos = (doc) => {
  const ev   = doc?.event || {};
  const fields = parseDesc(ev.description);
  const reqs = [];
  const img  = ev.imagenologia;
  const mue  = ev.muestrapatologica;

  if (img && typeof img === 'object') {
    if (img.arcoC)  reqs.push('Arco C');
    if (img.rayoxX) reqs.push('Rayos X');
  }
  if (Array.isArray(ev.materialEspecial) && ev.materialEspecial.length > 0)
    reqs.push('Material especial');
  if (Array.isArray(ev.rentaEquipos) && ev.rentaEquipos.length > 0)
    reqs.push('Renta de equipos');
  if (mue && typeof mue === 'object') {
    if (mue.postOperatorios) reqs.push('Muestra post-operatoria');
    if (mue.trasOperatorios) reqs.push('Muestra trans-operatoria');
  }
  // Campo propio e independiente de "Material especial" — capturado desde
  // el checkbox "¿Requiere de algún Material o Equipo especial?" del wizard
  const requerimientoEspecial = ev.requerimientoEspecial || fields['RequerimientoEspecial'];
  if (requerimientoEspecial && requerimientoEspecial !== 'NA')
    reqs.push('Requerimiento especial');
  return reqs;
};

/* ─────────────────────────────────────────────────────────── */

const useEventos = (calendar, rangoDesde, rangoHasta) => {
  const [docs,    setDocs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!calendar) return;
    setLoading(true);
    setError(null);

    const unsub = pacientesStore
      .collection(eventosCollection)
      .onSnapshot(
        (snap) => {
          if (!mounted.current) return;
          const list = [];
          snap.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
          setDocs(list);
          setLoading(false);
        },
        (err) => {
          if (!mounted.current) return;
          console.error('useEventos:', err);
          setError('No se pudieron cargar los agendamientos.');
          setLoading(false);
        }
      );

    return () => unsub();
  }, [calendar]);

  const { pendientes, confirmadas, historico, canceladas } = useMemo(() => {
    const desde = rangoDesde ? new Date(rangoDesde) : null;
    const hasta = rangoHasta ? new Date(rangoHasta) : null;

    // Solo eventos de este hospital con fechas válidas
    // (o de todas, si calendar === TODAS_LAS_UNIDADES)
    const delHospital = docs.filter((doc) => {
      if (calendar !== TODAS_LAS_UNIDADES && getUH(doc) !== calendar) return false;
      // Excluir si inicio o fin son fechas inválidas
      if (!getInicio(doc) || !getFin(doc)) return false;
      return true;
    });

    const dentroDelRango = (doc) => {
      const inicio = getInicio(doc);
      return (!desde || inicio >= desde) && (!hasta || inicio <= hasta);
    };

    // ── PENDIENTES ── (desde la fecha inicial seleccionada en adelante,
    // sin límite superior, para ver todas las pendientes a futuro;
    // ya no desaparecen automáticamente al pasar su hora)
    const pendientes = delHospital
      .filter((doc) => getSummary(doc) === 'pending' && (!desde || getInicio(doc) >= desde))
      .sort((a, b) => getInicio(a) - getInicio(b));

    // ── CONFIRMADAS ── (permanecen aquí hasta que se marquen como
    // Realizada o Cancelada; ya no se mueven solas al pasar su hora)
    const confirmadas = delHospital
      .filter((doc) => getSummary(doc) === 'confirmada' && dentroDelRango(doc))
      .sort((a, b) => getInicio(a) - getInicio(b));

    // ── HISTÓRICO ── (únicamente cirugías marcadas como Realizada)
    const historico = delHospital
      .filter((doc) => getSummary(doc) === 'realizada' && dentroDelRango(doc))
      .sort((a, b) => getFin(b) - getFin(a));

    // ── CANCELADAS ──
    const canceladas = delHospital
      .filter((doc) => getSummary(doc) === 'cancelada' && dentroDelRango(doc))
      .sort((a, b) => getFin(b) - getFin(a));

    return { pendientes, confirmadas, historico, canceladas };
  }, [docs, calendar, rangoDesde, rangoHasta]);

  return { pendientes, confirmadas, historico, canceladas, loading, error, todos: docs };
};

export default useEventos;